/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-08-07
 */

/**
 * Password strength validation utilities.
 * Provides consistent password strength checking across all forms.
 */

/**
 * Validates password strength and returns detailed feedback.
 * @param password - The password to validate
 * @returns Array of error messages for password requirements not met
 * @example
 * const errors = validatePasswordStrength('weak'); // ['Password must be at least 8 characters long', ...]
 * const errors = validatePasswordStrength('StrongPass123'); // []
 */
export const validatePasswordStrength = (password: string): string[] => {
  const passwordErrors: string[] = [];

  if (password.length < 8) {
    passwordErrors.push('Password must be at least 8 characters long');
  }

  if (!/[A-Z]/.test(password)) {
    passwordErrors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    passwordErrors.push('Password must contain at least one lowercase letter');
  }

  if (!/\d/.test(password)) {
    passwordErrors.push('Password must contain at least one number');
  }

  return passwordErrors;
};

/**
 * Calculates password strength score (0-4).
 * @param password - The password to evaluate
 * @returns Strength score from 0 (very weak) to 4 (strong)
 * @example
 * const score = getPasswordStrengthScore('weak'); // 0
 * const score = getPasswordStrengthScore('StrongPass123'); // 4
 */
export const getPasswordStrengthScore = (password: string): number => {
  const errors = validatePasswordStrength(password);
  return 4 - errors.length;
};

/**
 * Gets password strength label based on score.
 * @param score - The strength score (0-4)
 * @returns Human-readable strength label
 * @example
 * const label = getPasswordStrengthLabel(4); // 'Strong'
 * const label = getPasswordStrengthLabel(0); // 'Very Weak'
 */
export const getPasswordStrengthLabel = (score: number): string => {
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  return strengthLabels[score] || 'Very Weak';
};

/**
 * Gets password strength color based on score.
 * @param score - The strength score (0-4)
 * @returns Hex color code for the strength level
 * @example
 * const color = getPasswordStrengthColor(4); // '#4caf50' (green)
 * const color = getPasswordStrengthColor(0); // '#f44336' (red)
 */
export const getPasswordStrengthColor = (score: number): string => {
  const strengthColors = [
    '#f44336', // Very Weak - Red
    '#ff9800', // Weak - Orange
    '#ffeb3b', // Fair - Yellow
    '#8bc34a', // Good - Light Green
    '#4caf50', // Strong - Green
  ];
  return strengthColors[score] || '#f44336';
};
